import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  AnnouncementWithRelationsDto,
  UpdateAnnouncementDto,
  ResponseDto,
} from "@mimber/schema"
import { announcementsKeys } from "../announcements.utils"

interface UpdateAnnouncementOptions extends Omit<
  UseMutationOptions<
    ResponseDto<AnnouncementWithRelationsDto>,
    Error,
    UpdateAnnouncementDto
  >,
  "mutationFn"
> {}

export const useUpdateAnnouncement = (
  announcementUuid: string,
  options: UpdateAnnouncementOptions = {}
): UseMutationResult<
  ResponseDto<AnnouncementWithRelationsDto>,
  Error,
  UpdateAnnouncementDto
> => {
  guardAppUsage("mimber-organization", "useUpdateAnnouncement")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: UpdateAnnouncementDto) => {
      const response = await server.patch<
        ResponseDto<AnnouncementWithRelationsDto>
      >(`announcements/${announcementUuid}`, data)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: announcementsKeys.findAll(),
      })
      queryClient.invalidateQueries({
        queryKey: announcementsKeys.findOne(announcementUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
